import React, { useState, useEffect } from 'react';
import useDialog from '../hooks/useDialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/LanguageContext';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import './UsernameSelectionModal.css';

/**
 * UsernameSelectionModal - Modal for selecting a username after social sign-in
 *
 * Shows when a user signs in with a social provider and needs to choose a username.
 */
function UsernameSelectionModal() {
    const { t } = useTranslation();
    const { pendingSocialAuth, completeSocialAuthWithUsername } = useAuth();
    const [username, setUsername] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useBodyScrollLock(!!pendingSocialAuth);
    const dialogRef = useDialog(!!pendingSocialAuth, undefined);

    // Generate a suggested username from the social auth data
    useEffect(() => {
        if (pendingSocialAuth && !username) {
            let suggested = '';
            if (pendingSocialAuth.email) {
                suggested = pendingSocialAuth.email.split('@')[0];
            } else if (pendingSocialAuth.name) {
                suggested = pendingSocialAuth.name.replace(/\s+/g, '_').toLowerCase();
            }
            setUsername(suggested);
        }
    }, [pendingSocialAuth, username]);

    if (!pendingSocialAuth) {
        return null;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validate username
        if (!username.trim()) {
            setError(t('auth.errors.usernameRequired'));
            return;
        }

        if (username.length < 3) {
            setError(t('auth.errors.usernameTooShort'));
            return;
        }

        if (username.length > 20) {
            setError(t('auth.errors.usernameTooLong'));
            return;
        }

        if (!/^[a-zA-Z0-9_]+$/.test(username)) {
            setError(t('auth.errors.usernameInvalidChars'));
            return;
        }

        setLoading(true);

        try {
            const result = await completeSocialAuthWithUsername(username);

            if (!result.success) {
                if (result.code === 'username_taken') {
                    setError(t('auth.errors.usernameTaken'));
                } else {
                    setError(result.error || t('auth.errors.completeSignInFailed'));
                }
                setLoading(false);
            }
            // If successful, the modal will close automatically via context update
        } catch (err) {
            setError(err.message || t('auth.errors.generic'));
            setLoading(false);
        }
    };

    const handleUsernameChange = (e) => {
        const value = e.target.value;
        // Only allow alphanumeric and underscore
        if (/^[a-zA-Z0-9_]*$/.test(value)) {
            setUsername(value);
            setError('');
        }
    };

    return (
        <div className="modal-overlay-base username-modal-overlay">
            <div className="modal-content-base username-modal" data-modal-scrollable ref={dialogRef} role="dialog" aria-modal="true">
                <div className="username-modal-header">
                    <h2>{t('auth.usernameModal.title')}</h2>
                    <p>{t('auth.usernameModal.subtitle')}</p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="username-input-group">
                        <label htmlFor="username">{t('auth.username')}</label>
                        <input
                            type="text"
                            id="username"
                            value={username}
                            onChange={handleUsernameChange}
                            placeholder={t('auth.usernameModal.placeholder')}
                            disabled={loading}
                            maxLength={20}
                        />
                        <div className="username-requirements">
                            <span className={username.length >= 3 ? 'valid' : ''}>
                                <FontAwesomeIcon icon={username.length >= 3 ? faCheck : faTimes} />
                                {t('auth.usernameModal.minLength')}
                            </span>
                            <span className={/^[a-zA-Z0-9_]+$/.test(username) && username ? 'valid' : ''}>
                                <FontAwesomeIcon icon={/^[a-zA-Z0-9_]+$/.test(username) && username ? faCheck : faTimes} />
                                {t('auth.usernameModal.allowedChars')}
                            </span>
                        </div>
                    </div>

                    {error && (
                        <div className="username-error">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        className="username-submit-button"
                        disabled={loading || !username || username.length < 3}
                    >
                        {loading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>{t('auth.usernameModal.creatingAccount')}</span>
                            </>
                        ) : (
                            <span>{t('common.continue')}</span>
                        )}
                    </button>
                </form>

                <p className="username-note">
                    {t('auth.usernameModal.changeLater')}
                </p>
            </div>
        </div>
    );
}

export default UsernameSelectionModal;
